import { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { format } from 'date-fns';

import { Device } from 'models';
import GhostIcon from 'assets/vectors/ghost.svg';
import * as routes from 'router/Routes';

import Card from 'components/UI/Card';
import CardHeader from 'components/UI/Card/CardHeader';
import InfoItem from 'components/UI/InfoItem';
import {
  ContentContainer,
  NameContainer,
  DetailGroup,
  Image,
  Title,
  CardHeaderTop,
  CardHeaderTopColumn,
} from './styled';
import Button from 'components/UI/Button';
import EditDeviceModal from '../EditDeviceModal';
import Tooltip from 'components/UI/Tooltip';
import { useAccess } from 'hooks/useAccess';
import Loader from 'components/UI/Loader';

type Props = {
  device: Device | null;
  isLoading: boolean;
};

const DeviceInfoCard: FC<Props> = ({ device, isLoading }) => {
  const intl = useIntl();
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const { updateDeviceAccess } = useAccess();

  const onOpen = useCallback(() => {
    setOpenEditModal(true);
  }, [setOpenEditModal]);

  const onClose = useCallback(() => {
    setOpenEditModal(false);
  }, [setOpenEditModal]);

  // Use image url or default image
  const renderImage = useCallback((url?: string) => {
    if (url) {
      return <Image src={url} />;
    }
    return <Image src={GhostIcon} alt="shadow" />;
  }, []);

  // Details
  const details = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
    if (!device) {
      return null;
    }
    const {
      articleDesc,
      pnc,
      serialNumber,
      status,
      lastConnection,
      iccid,
      hasPsks,
      isTestDevice,
      testDeviceDescription,
    } = device.attributes;
    // Detail list
    const detailList = [
      {
        id: 1,
        title: 'connectivity.articleDesc',
        text: articleDesc,
        conditional: false,
      },
      {
        id: 2,
        title: 'connectivity.articleNo',
        text: pnc,
        conditional: false,
      },
      {
        id: 3,
        title: 'connectivity.serialNo',
        text: serialNumber,
        conditional: false,
      },

      {
        id: 4,
        title: 'connectivity.status',
        text: status,
        conditional: false,
      },
      {
        id: 5,
        title: 'connectivity.lastConnection',
        text:
          lastConnection && lastConnection > 0
            ? format(Number(lastConnection), 'yyyy-MM-dd HH:mm:ss')
            : '-',
        conditional: false,
      },
      {
        id: 6,
        title: 'connectivity.simcard',
        text: iccid,
        conditional: true,
      },
      {
        id: 7,
        title: 'connectivity.psks',
        text: hasPsks ? 'yes' : 'no',
        conditional: true,
      },
      {
        id: 8,
        title: 'connectivity.testDevice',
        text: isTestDevice ? testDeviceDescription || 'yes' : undefined,
        conditional: !isTestDevice,
        tooltip: 'connectivity.testDeviceExplanation',
      },
    ];

    const details = detailList.map(
      ({ id, title, text, conditional, tooltip }) => {
        if (!text && conditional) {
          return null;
        }
        if (tooltip) {
          return (
            <div key={id}>
              <Tooltip paragraph={intl.formatMessage({ id: tooltip })}>
                <InfoItem
                  key={id}
                  title={intl.formatMessage({ id: title })}
                  value={text || '–'}
                />
              </Tooltip>
            </div>
          );
        }
        return (
          <InfoItem
            key={id}
            title={intl.formatMessage({ id: title })}
            value={text || '–'}
          />
        );
      },
    );
    return (
      <ContentContainer>
        {renderImage(device?.attributes.imageUrl)}
        <NameContainer>
          <CardHeaderTop>
            <CardHeaderTopColumn>
              <Title>{device?.id}</Title>
            </CardHeaderTopColumn>
            <CardHeaderTopColumn>
              {updateDeviceAccess && (
                <Button
                  backgroundColor="transparent"
                  color="primary"
                  onClick={onOpen}
                >
                  <FormattedMessage id="connectivity.edit_btn" />
                </Button>
              )}
            </CardHeaderTopColumn>
          </CardHeaderTop>
          <DetailGroup>{details}</DetailGroup>
        </NameContainer>
      </ContentContainer>
    );
  }, [intl, device, isLoading, onOpen, renderImage, updateDeviceAccess]);

  return (
    <Card>
      <CardHeader navUrl={routes.DEVICES} title="connectivity.title" />
      {details}
      <EditDeviceModal device={device} open={openEditModal} onClose={onClose} />
    </Card>
  );
};

export default DeviceInfoCard;
